import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, TextField, CircularProgress, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions,  Card, CardContent} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ViewOlympiadClubbingModal from "../../components/ViewOlympiadClubbingModal"; // adjust path if different
import HomeIcon from '@mui/icons-material/Home';
// Adjust to match your actual backend base URL if not proxied

const OlympiadVerificationPage = () => {
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modalData, setModalData] = useState(null);
    const [currentStatus, setCurrentStatus] = useState("pending");

    // done -> after confirm/update-request submitted
    const [mode, setMode] = useState("view");

    const [updateFormOpen, setUpdateFormOpen] = useState(false);
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

      const themeColors = {
    primaryBlue: '#0A2540',    // Deep navy blue used for headings and branding
    textSecondary: '#637381',  // Soft grey for secondary descriptions
    backgroundGrey: '#F4F6F8', // Subtle off-white page background
  };

    const fetchDetails = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${window.Platform.database.funcForBaseURL()}/olympiadVerification/verify/${token}`);
            const result = await res.json();

            if (result.status) {
                setModalData(result.data.modalData);
                setCurrentStatus(result.data.currentStatus);

                if (result.data.currentStatus && result.data.currentStatus !== "pending") {
                    setMode("done");
                }
            } else {
                setError(result.message || "This verification link is invalid or has expired.");
            }
        } catch (e) {
            console.error(e);
            setError("Something went wrong while loading your registration details. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        setError("");
        try {
            const res = await fetch(`${window.Platform.database.funcForBaseURL()}/olympiadVerification/verify/${token}/confirm`, { method: "POST" });
            const result = await res.json();

            if (result.status) {
                setMode("done");
            } else {
                setError(result.message || "Unable to submit your confirmation. Please try again.");
            }
        } catch (e) {
            console.error(e);
            setError("Unable to submit your confirmation. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitUpdateRequest = async () => {
        if (!remark || remark.trim() === "") {
            setError("Please describe what needs to be corrected before submitting.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const res = await fetch(`${window.Platform.database.funcForBaseURL()}/olympiadVerification/verify/${token}/requestUpdate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updatedData: { remark } }),
            });
            const result = await res.json();

            if (result.status) {
                setUpdateFormOpen(false);
                setMode("done");
            } else {
                setError(result.message || "Unable to submit your update request. Please try again.");
            }
        } catch (e) {
            console.error(e);
            setError("Unable to submit your update request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error && !modalData) {
        return (
            <Box maxWidth={520} mx="auto" mt={10} px={2}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (mode === "done") {
        return (
            // <Box maxWidth={520} mx="auto" mt={10} px={2}>
            //     <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="success">
            //         Thank you! Your response has been recorded successfully.
            //     </Alert>
            // </Box>
              <Box 
      sx={{ 
        minHeight: '80vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: themeColors.backgroundGrey,
        px: 2,
        py: 6
      }}
    >
      <Box maxWidth={540} width="100%" mx="auto">
        <Card 
          elevation={3} 
          sx={{ 
            borderRadius: 4, 
            textAlign: 'center', 
            p: { xs: 2, sm: 4 },
            borderTop: `6px solid ${themeColors.primaryBlue}` 
          }}
        >
          <CardContent>
            {/* Success Alert Block */}
            <Alert 
              icon={<CheckCircleOutlineIcon fontSize="medium" />} 
              severity="success"
              variant="filled"
              sx={{ 
                borderRadius: 2,
                mb: 4,
                justifyContent: 'center',
                fontSize: '1rem',
                fontWeight: 500,
                backgroundColor: '#2e7d32' // Solid green for definitive confirmation
              }}
            >
              Thank you! Your response has been recorded successfully.
            </Alert>

            {/* Mittsure-Themed Heading & Subtext */}
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700, 
                color: themeColors.primaryBlue,
                letterSpacing: '-0.5px'
              }}
            >
              Submission Confirmed
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                color: themeColors.textSecondary, 
                mb: 4,
                lineHeight: 1.6
              }}
            >
              We appreciate you taking the time to share your response with us. 
              Our team will review your inputs to help enhance your digital learning experience.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f7fb" }}>
            {/* Same summary view the admin sees */}
            <ViewOlympiadClubbingModal
                modalData={modalData}
                closePopup={() => {}} // no close on a standalone public page
            />

            {/* Verification action bar — sits below the summary */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    background: "#fff",
                    borderTop: "2px solid #ddd",
                    p: 3,
                }}
            >
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                    Please confirm the details above are correct
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={submitting}
                        onClick={handleConfirm}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : "Confirm, details are correct"}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditNoteIcon />}
                        disabled={submitting}
                        onClick={() => {
                            setError("");
                            setUpdateFormOpen(true);
                        }}
                    >
                        Info is incorrect, request update
                    </Button>
                </Stack>
            </Box>

            {/* Update-request remark dialog */}
            <Dialog open={updateFormOpen} onClose={() => setUpdateFormOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>What needs to be corrected?</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mt: 1 }}
                        label="Describe the correction needed"
                        placeholder="e.g. Student count should be 120 instead of 100, coordinator mobile number is incorrect..."
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateFormOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={submitting} onClick={handleSubmitUpdateRequest}>
                        {submitting ? <CircularProgress size={20} color="inherit" /> : "Submit Update Request"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OlympiadVerificationPage;